import { View, Image } from "@/components/ui";
import type { ServiceName } from "@/types/service";
import { Column, Container } from "@/components/ui/layout";
import { ServiceFaqSection } from "./service-faq-section";
import { ServiceHeroSection } from "./service-hero-section";
import { ServiceDetailSection } from "./service-detail-section";
import { CustomerReviews } from "./customer-reviews";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { SectionWrapper } from "@/components/shared/sticky-scroll-navbar";
import { useBookingRouterInterceptor } from "@/components/hooks/use-booking-route";
import { getPlatform, safeGet } from "@/lib/utils";
import { useEffect, useMemo } from "react";
import {
  defaultServiceDetails,
  useBookingState,
} from "@/store/booking/useBookingState";
import {
  PackageFrequencyGroupEnum,
  type ServiceDetail as IServiceDetail,
} from "@/types/booking";
import { ServiceDetailNavigation } from "./service-detail-navigation";
import { getDepartment, getServiceUnit } from "@/lib/booking-lib";
import type { ServiceDetailKey } from "@/constants/booking";
import { serviceDetailCover } from "@/constants/booking";
import { ChoosePackageModal } from "@/components/shared/booking/choose-package-modal";
import { WhatsappSupportBanner } from "@/components/shared/whatsapp-support-banner";
import { onChatSales } from "@/lib/chat-sales";
import {
  PackageDepartmentEnum,
  PackageDetailSortByEnum,
  PackageRecurrenceEnum,
  PackageUnitTypeEnum,
  ServiceTypeEnum,
  SortEnum,
} from "@/__generated__/graphql";
import { useAllClientPackageDetailsStore } from "@/store/booking/allClientPackageDetails";
import { Typography } from "@/components/shared/typography";
import { formatPrice } from "@/lib/helpers/number";
import { IconButton } from "@/components/ui/icon-button";
import { useRoute } from "@/components/shared/router";
import { BackToTop } from "@/components/shared/back-to-top";
import { useScrollToTop } from "@/components/hooks/use-scroll-to-top";
import { ChooseMassagePackageModal } from "@/components/shared/booking/choose-massage-package-modal";
import { ChoosePetCarePackageModal } from "@/components/shared/booking/choose-pet-care-package-modal";
import { DEFAULT_WHATSAPP_MESSAGE } from "@/constants";

export function ServiceDetail({ name }: { name: ServiceName }) {
  const { mediaQuery, isMobile } = useWindowDimensions();
  const { isVisible, scrollToTop } = useScrollToTop();
  const platform = getPlatform();
  const { pull } = useRoute();
  useBookingRouterInterceptor();
  const {
    data: { serviceDetails, packageDetailsFilter },
    setBookingState,
  } = useBookingState();
  const { fetch: getPackages } = useAllClientPackageDetailsStore();

  const detailKey = (
    name === "aircon-service"
      ? `${name}-${packageDetailsFilter?.serviceTypes?.[0]}`
      : name
  ) as ServiceDetailKey;

  const pageServiceDetails = useMemo(() => {
    return safeGet(
      serviceDetails,
      detailKey,
      defaultServiceDetails,
    ) as IServiceDetail;
  }, [detailKey]);

  const initHomeBeauty = () => {
    setBookingState({
      isRebook: false,
      department: PackageDepartmentEnum.HomeBeauty,
      choosePackageLoading: true,
      packageDetailsFilter: {
        serviceTypes: [ServiceTypeEnum.Combo],
        repeatEvery: [PackageRecurrenceEnum.AdHoc],
      },
    });

    getPackages({
      requestPayload: {
        filters: {
          active: true,
          sort: [SortEnum.Asc],
          sortBy: [PackageDetailSortByEnum.Duration],
          serviceTypes: [
            ServiceTypeEnum.Combo,
            ServiceTypeEnum.Manicure,
            ServiceTypeEnum.Pedicure,
            ServiceTypeEnum.AddOn,
            ServiceTypeEnum.Upgrade,
            ServiceTypeEnum.Discount,
          ],
          department: PackageDepartmentEnum.HomeBeauty,
        },
      },
    })
      .then((res) => {
        if (!res.data || res.error) {
          return;
        }
        setBookingState({
          packageDetails: res.data,
        });
      })
      .finally(() => {
        setBookingState({
          choosePackageLoading: false,
        });
      });
  };

  const initLashes = () => {
    setBookingState({
      isRebook: false,
      department: PackageDepartmentEnum.HomeBeauty,
      choosePackageLoading: true,
      packageDetailsFilter: {
        serviceTypes: [ServiceTypeEnum.Lashes, ServiceTypeEnum.AddOn],
        repeatEvery: [PackageRecurrenceEnum.AdHoc],
      },
    });

    getPackages({
      requestPayload: {
        filters: {
          active: true,
          sort: [SortEnum.Asc],
          sortBy: [PackageDetailSortByEnum.Duration],
          serviceTypes: [ServiceTypeEnum.Lashes, ServiceTypeEnum.AddOn],
          department: PackageDepartmentEnum.HomeBeauty,
        },
      },
    })
      .then((res) => {
        if (!res.data || res.error) {
          return;
        }
        setBookingState({
          packageDetails: res.data,
        });
      })
      .finally(() => {
        setBookingState({
          choosePackageLoading: false,
        });
      });
  };

  const initHomeMassage = () => {
    setBookingState({
      department: PackageDepartmentEnum.Massage,
      packageDetailsFilter: {
        repeatEvery: [PackageRecurrenceEnum.AdHoc],
        serviceTypes: [ServiceTypeEnum.Massage],
        unitTypes: [PackageUnitTypeEnum.BodyMassage],
      },
    });

    getPackages({
      requestPayload: {
        filters: {
          active: true,
          department: PackageDepartmentEnum.Massage,
          unitTypes: [
            PackageUnitTypeEnum.BodyMassage,
            PackageUnitTypeEnum.FootMassage,
          ],
        },
      },
    }).then((res) => {
      if (!res.data || res.error) {
        return;
      }
      setBookingState({
        packageDetails: res.data,
      });
    });
  };

  const initPetGrooming = () => {
    setBookingState({
      department: PackageDepartmentEnum.PetCare,
      packageDetailsFilter: {
        repeatEvery: [PackageRecurrenceEnum.AdHoc],
        serviceTypes: [ServiceTypeEnum.PetCare],
        unitTypes: [PackageUnitTypeEnum.Cat],
      },
    });

    getPackages({
      requestPayload: {
        filters: {
          active: true,
          department: PackageDepartmentEnum.PetCare,
          unitTypes: [
            PackageUnitTypeEnum.Cat,
            PackageUnitTypeEnum.Dog,
            PackageUnitTypeEnum.All,
          ],
        },
      },
    }).then((res) => {
      if (!res.data || res.error) {
        return;
      }
      setBookingState({
        packageDetails: res.data,
      });
    });
  };

  const initPetSitting = () => {
    setBookingState({
      packageDetailsFilter: {
        repeatEvery: [PackageRecurrenceEnum.AdHoc],
        repeatEveryTimes: 1,
        frontEndFrequency: PackageFrequencyGroupEnum.AD_HOC,
      },
    });

    getPackages({
      requestPayload: {
        filters: {
          active: true,
          department: PackageDepartmentEnum.PetCare,
          serviceTypes: [ServiceTypeEnum.PetSitting],
        },
      },
    }).then((res) => {
      if (!res.data || res.error) {
        return;
      }
      setBookingState({
        packageDetails: res.data,
      });
    });
  };

  const { title, bookingTotal, descriptions, detailImages, faqs, startPrice } =
    pageServiceDetails;

  useEffect(() => {
    setBookingState({
      department: getDepartment(name),
      selectedPackageIds: [],
      selectedAddress: null,
      selectedWorkers: [],
      isRebook: false,
      elderCareProfileInfo: null,
      babyProfileInfo: null,
    });

    if (name === "home-beauty") {
      initHomeBeauty();
    }
    if (name === "home-massage") {
      initHomeMassage();
    }
    if (name === "pet-grooming") {
      initPetGrooming();
    }
    if (name === "pet-sitting") {
      initPetSitting();
    }
    if (name === "lashes") {
      initLashes();
    }
  }, []);

  const getServiceDetailCoverImage = (detailKey: ServiceDetailKey) => {
    return serviceDetailCover[detailKey] ?? serviceDetailCover["home-cleaning"];
  };

  return (
    <View className="relative py-0 md:py-6">
      <Container>
        <SectionWrapper keyName="service" className="relative w-full">
          <Image
            src={getServiceDetailCoverImage(detailKey)}
            className="relative h-[211px] w-full overflow-hidden rounded-none object-cover md:h-[531px] md:rounded-xl"
            height={isMobile ? 211 : 531}
          />
          <IconButton
            className="z-2 absolute left-5 top-5 bg-black/50 web:hidden"
            rounded="full"
            iconName="arrowLeft"
            color="white"
            onClick={pull}
          />
        </SectionWrapper>
      </Container>
      <Container>
        <Column desktop="8">
          <View className="relative flex flex-col gap-4">
            <ServiceHeroSection
              title={title}
              name={name}
              bookingTotal={bookingTotal}
              descriptions={descriptions}
              startPrice={startPrice}
            />

            {mediaQuery === "mobile" && (
              <SectionWrapper keyName="reviews">
                <CustomerReviews name={name} />
              </SectionWrapper>
            )}

            <SectionWrapper keyName="details">
              <ServiceDetailSection detailImages={detailImages} />
            </SectionWrapper>

            <SectionWrapper keyName="faqs" className="native:mb-[80px] mb-10">
              <ServiceFaqSection faqs={faqs} />
            </SectionWrapper>
          </View>
          <BackToTop isVisible={isVisible} onScrollToTop={scrollToTop} />

          <ChoosePackageModal name={name} />
          <ChooseMassagePackageModal />
          <ChoosePetCarePackageModal />
        </Column>

        <Column desktop="4">
          {platform === "web" && (
            <View className="relative flex flex-col gap-4 py-4 md:sticky md:top-14">
              <View className="hidden flex-row items-center justify-between md:flex">
                <Typography color="foreground-intermediate" variant="body-sm">
                  Starting from
                </Typography>
                <View className="flex flex-row items-center">
                  <Typography
                    variant="h3"
                    color="brand-primary"
                  >{`${formatPrice(startPrice)}`}</Typography>
                  <Typography
                    variant="body-sm"
                    color="foreground-intermediate"
                  >{` /${getServiceUnit(name)}`}</Typography>
                </View>
              </View>
              <ServiceDetailNavigation name={name} />
              {mediaQuery !== "mobile" && (
                <View className="flex flex-col gap-4">
                  <CustomerReviews name={name} />
                  <WhatsappSupportBanner
                    onClick={() => onChatSales(true, DEFAULT_WHATSAPP_MESSAGE)}
                  />
                </View>
              )}
            </View>
          )}
        </Column>
      </Container>
    </View>
  );
}
